import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Sun, Thermometer, Target, SunDim, Lock, UtensilsCrossed, Music, Gem, TreePine, Droplets, GlassWater, Check, Landmark, Shield, FileCheck, Menu, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = '/api/uploads/navanaa_logo_clean.png';
const FLOOR_GF_URL = 'https://customer-assets.emergentagent.com/job_content-hub-1062/artifacts/xdj8xjtd_Screenshot%202026-03-25%20at%207.28.33%E2%80%AFPM.png';
const FLOOR_FF_URL = 'https://customer-assets.emergentagent.com/job_content-hub-1062/artifacts/uwljn51o_Screenshot%202026-03-25%20at%207.28.47%E2%80%AFPM.png';
const FULL_LOGO = `${API}${LOGO_URL}`;

const ARCH_FALLBACK = [
  'https://customer-assets.emergentagent.com/job_content-hub-1062/artifacts/ug8dgwv4_Navanaa%20Estate%20House%201%20Landscape.png',
  'https://customer-assets.emergentagent.com/job_content-hub-1062/artifacts/fk74mcgt_Navanaa%20Estate%20House%201%20%20landscape%202%20.png',
  'https://customer-assets.emergentagent.com/job_content-hub-1062/artifacts/ug8dgwv4_Navanaa%20Estate%20House%201%20Landscape.png',
];

const resolveUrl = (src) => (src && src.startsWith('/') ? `${API}${src}` : src);

// ─── Small reusable components ───────────────────────────────────────────────
const Placeholder = ({ text }) => <div className="nv-placeholder-img"><span>{text || 'Navanaa Estate'}</span></div>;
const Img = ({ src, alt, text }) => src ? <img src={resolveUrl(src)} alt={alt || ''} /> : <Placeholder text={text} />;

const ICON_MAP = { sun: Sun, thermometer: Thermometer, target: Target, 'sun-dim': SunDim, lock: Lock, utensils: UtensilsCrossed, music: Music, stone: Gem, wood: TreePine, clay: Droplets, glass: GlassWater };
const SysIcon = ({ name }) => { const I = ICON_MAP[name]; return I ? <I size={22} /> : <span style={{ fontSize: '22px' }}>&#9672;</span>; };

const TRUST_ICONS = { bank: Landmark, shield: Shield, 'file-check': FileCheck };

// ─── Mobile Menu ─────────────────────────────────────────────────────────────
function MobileMenu({ open, onClose, go }) {
  // Prevent body scroll when menu open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const nav = (id) => { onClose(); setTimeout(() => go(id), 300); };

  return (
    <div className={`nv-mobile-menu ${open ? 'open' : ''}`} aria-hidden={!open}>
      <button onClick={() => nav('about')}>About</button>
      <button onClick={() => nav('estate')}>Estate</button>
      <button onClick={() => nav('architecture')}>Architecture</button>
      <button onClick={() => nav('gallery')}>Gallery</button>
      <button onClick={() => nav('location')}>Location</button>
      <Link to="/projects" onClick={onClose}>Projects</Link>
      <button className="nv-mobile-menu-cta" onClick={() => nav('contact')}>Enquire Now</button>
    </div>
  );
}

// ─── Section sub-components ──────────────────────────────────────────────────

function HeroSection({ data, go }) {
  if (!data) return null;
  return (
    <section className="nv-hero" id="hero" data-testid="hero-section">
      <div className="nv-hero-bg" style={data.image ? { backgroundImage: `url(${resolveUrl(data.image)})` } : {}} />
      <div className="nv-hero-overlay" />
      <div className="nv-hero-content">
        <div className="nv-eyebrow-hero">{data.eyebrow}</div>
        <h1 className="nv-h1">House of Your <br /><em>Dreams</em> in the <br /><em>Himalayas</em></h1>
        <p className="nv-hero-sub">{data.subtitle}</p>
        <div>
          <a className="nv-btn-gold" onClick={() => go('contact')} data-testid="hero-enquire-btn">Know More</a>
          <a className="nv-btn-ghost" onClick={() => go('about')}>Explore Estate</a>
        </div>
      </div>
      {data.badge_number && (
        <div className="nv-hero-badge">
          <div className="nv-badge-num">{data.badge_number}</div>
          <div className="nv-badge-lbl">{data.badge_label}</div>
        </div>
      )}
    </section>
  );
}

function WhyNavanaaSection() {
  const reasons = [
    { id: '01', label: 'Timeline', title: 'Ready to move in 12 months' },
    { id: '02', label: 'Founders', title: 'Project by founders featured on Shark Tank India 2024' },
    { id: '03', label: 'Compliance', title: 'Fully approved by local authorities' },
    { id: '04', label: 'Financing', title: 'Home loans available through our banking partners' },
    { id: '05', label: 'Track Record', title: 'Previously delivered projects for Azim Premji Foundation' },
    { id: '06', label: 'Returns', title: 'High ROI with 25%–50% YoY growth' },
  ];

  return (
    <section className="nv-why">
      <div className="nv-section-inner">
        <div className="nv-why-head">
          <div>
            <div className="nv-eyebrow">Why Navanaa</div>
            <h2 className="nv-why-title">Built on trust, designed to deliver</h2>
          </div>
          <div className="nv-why-copy">
            Six reasons Navanaa Estate stands apart —
            from approvals already in hand to a founding
            team with a record of delivery.
          </div>
        </div>
        <div className="nv-why-grid">
          {reasons.map((item) => (
            <div key={item.id} className="nv-why-card">
              <div className="nv-why-top">
                <span className="nv-why-line" />
                <span className="nv-why-number">{item.id}</span>
              </div>
              <div className="nv-why-label">{item.label}</div>
              <h3 className="nv-why-card-title">{item.title}</h3>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TrustSection({ data }) {
  if (!data?.items) return null;
  return (
    <section className="nv-trust" data-testid="trust-section">
      <div className="nv-section-inner">
        <div className="nv-trust-grid nv-reveal">
          {data.items.map((item, i) => {
            const Icon = TRUST_ICONS[item.icon] || FileCheck;
            return (
              <div className="nv-trust-card" key={`trust-${item.title}-${i}`}>
                <div className="nv-trust-icon"><Icon size={30} /></div>
                <div className="nv-trust-title">{item.title}</div>
                <div className="nv-trust-desc">{item.desc}</div>
                {item.badge && <div className="nv-trust-badge">{item.badge}</div>}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FoundersSection({ data }) {
  if (!data) return null;
  return (
    <section className="nv-founders" id="founders" data-testid="founders-section">
      <div className="nv-section-inner">
        <div className="nv-eyebrow center nv-reveal">{data.eyebrow || 'A Project By'}</div>
        <h2 className="nv-h2 dark nv-reveal" style={{ textAlign: 'center' }}>{data.title || 'Shark Tank India Founders'}</h2>
        <p className="nv-body nv-reveal" style={{ textAlign: 'center', margin: '0 auto' }}>{data.subtitle}</p>
        <div className="nv-sharktank-badge nv-reveal" style={{ justifyContent: 'center', display: 'flex', margin: '0 auto' }}>
          <span>As Seen on Shark Tank India &middot; Rs 2.5 Cr Deal</span>
        </div>
        <div className="nv-founders-grid nv-reveal">
          {data.founders?.map((f) => (
            <div className="nv-founder-card" key={`founder-${f.name}`}>
              {f.image && <div className="nv-founder-img"><img src={resolveUrl(f.image)} alt={f.name} /></div>}
              <div className="nv-founder-name">{f.name}</div>
              <span className="nv-founder-role">{f.role}</span>
              <div className="nv-founder-bio">{f.bio}</div>
            </div>
          ))}
        </div>
        <div className="nv-ventures nv-reveal">
          {data.ventures?.map((v) => (
            <div className="nv-venture" key={`venture-${v.name}`}>
              <div className="nv-venture-name">{v.name}</div>
              <div className="nv-venture-desc">{v.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ContactSection({ data, cf, setCf, onSubmit, cfLoading, cfMsg }) {
  if (!data) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('Name', cf.name);
      formData.append('Email', cf.email);
      formData.append('Phone', cf.phone);
      formData.append('Message', cf.message);
      formData.append('_subject', 'New Enquiry from Navanaa Estate Website');
      formData.append('_captcha', 'false');
      await fetch('https://formsubmit.co/akshay@personate.ai,cmo@kiwikisanwindow.com', {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json' },
      });
      alert('Thank you! Your enquiry has been sent successfully.');
      setCf({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error(error);
      alert('Failed to send enquiry. Please try again.');
    }
  };

  return (
    <section className="nv-contact" id="contact" data-testid="contact-section">
      <div className="nv-section-inner">
        <div className="nv-contact-vis nv-reveal">
          <Img src={data.image} alt="Navanaa Estate" text="Navanaa Estate" />
          <div className="nv-contact-overlay" />
          <div className="nv-contact-brand">
            <img src={FULL_LOGO} alt="Navanaa Estate" className="nv-contact-brand-logo" />
          </div>
        </div>

        <div>
          <div className="nv-eyebrow nv-reveal">Contact Us</div>
          <h2 className="nv-h2 light nv-reveal">{data.title || 'Begin the Conversation'}</h2>
          <p className="nv-body light nv-reveal">{data.body}</p>

          <div className="nv-reveal" style={{ marginTop: 28 }}>
            {data.email && (
              <div className="nv-contact-grp">
                <div className="nv-contact-lbl">Email</div>
                <a href={`mailto:${data.email}`} className="nv-contact-val">{data.email}</a>
              </div>
            )}
            {data.phone && (
              <div className="nv-contact-grp">
                <div className="nv-contact-lbl">Phone</div>
                <a href={`tel:${data.phone.replace(/\s/g, '')}`} className="nv-contact-val">{data.phone}</a>
              </div>
            )}
            {data.location && (
              <div className="nv-contact-grp">
                <div className="nv-contact-lbl">Location</div>
                <span className="nv-contact-val">{data.location}</span>
              </div>
            )}
            {data.instagram && (
              <div className="nv-contact-grp">
                <div className="nv-contact-lbl">Instagram</div>
                <span className="nv-contact-val">{data.instagram}</span>
              </div>
            )}
          </div>

          <form className="nv-contact-form nv-reveal" onSubmit={handleSubmit} data-testid="contact-form">
            <input
              type="text" name="Name" placeholder="Your Name"
              value={cf.name} onChange={(e) => setCf({ ...cf, name: e.target.value })}
              required data-testid="contact-name-input"
            />
            <input
              type="email" name="Email" placeholder="Your Email"
              value={cf.email} onChange={(e) => setCf({ ...cf, email: e.target.value })}
              required data-testid="contact-email-input"
            />
            <input
              type="tel" name="Phone" placeholder="Phone Number (optional)"
              value={cf.phone} onChange={(e) => setCf({ ...cf, phone: e.target.value })}
              data-testid="contact-phone-input"
            />
            <textarea
              name="Message" placeholder="Your Message"
              value={cf.message} onChange={(e) => setCf({ ...cf, message: e.target.value })}
              required data-testid="contact-message-input"
            />
            <button type="submit" className="nv-btn-gold" disabled={cfLoading} data-testid="contact-submit-btn">
              {cfLoading ? 'Sending...' : 'Send Enquiry'}
            </button>
            {cfMsg && <div className="nv-form-success" data-testid="contact-success">{cfMsg}</div>}
          </form>

          <div className="nv-contact-rera nv-reveal">
            RERA Registration: {data.rera}
            <br />
            All renders are indicative. Final specifications subject to design development.
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

export default function Website() {
  const [s, setS] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [loaderDone, setLoaderDone] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [cf, setCf] = useState({ name: '', email: '', phone: '', message: '' });
  const [cfMsg, setCfMsg] = useState('');
  const [cfLoading, setCfLoading] = useState(false);
  const [activeFloor, setActiveFloor] = useState('ground');
  const obsRef = useRef(null);
  const lifestyleImages = [
    "https://pub-c34aedad6b2e43ce903e45d5e600e33d.r2.dev/navanaa/sunlight.webp",
    "https://pub-c34aedad6b2e43ce903e45d5e600e33d.r2.dev/navanaa/slow%20work.webp",
    "https://pub-c34aedad6b2e43ce903e45d5e600e33d.r2.dev/navanaa/conversation.webp",
    "https://pub-c34aedad6b2e43ce903e45d5e600e33d.r2.dev/navanaa/silence.webp"
  ]

  const fetchData = useCallback(async () => {
    try {
      const [secRes, galRes] = await Promise.all([
        axios.get(`${API}/api/sections`, { headers: { 'Cache-Control': 'no-cache' } }),
        axios.get(`${API}/api/gallery`, { headers: { 'Cache-Control': 'no-cache' } }),
      ]);
      setS(secRes.data);
      setGallery(galRes.data);
    } catch (_e) { /* network error — silent for visitors */ }
  }, []);

  useEffect(() => {
    fetchData();
    const t = setTimeout(() => setLoaderDone(true), 2200);
    return () => clearTimeout(t);
  }, [fetchData]);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 80);
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => {
    if (!loaderDone || !s) return;
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('vis'); }),
      { threshold: 0.09, rootMargin: '0px 0px -40px 0px' }
    );
    obsRef.current = obs;
    document.querySelectorAll('.nv-reveal').forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, [loaderDone, s]);

  const go = useCallback((id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const submitContact = useCallback(async (e) => {
    e.preventDefault();
    setCfLoading(true);
    try {
      const r = await axios.post(`${API}/api/contact`, cf);
      setCfMsg(r.data.message);
      setCf({ name: '', email: '', phone: '', message: '' });
    } catch (_err) {
      setCfMsg('Something went wrong. Please try again.');
    }
    setCfLoading(false);
  }, [cf]);

  if (!s) return (
    <div className="nv-loader">
      <div className="nv-loader-logo">
        <img src={FULL_LOGO} alt="Navanaa Estate" className="nv-loader-logo-img" />
      </div>
      <div className="nv-loader-bar" />
    </div>
  );

  const fp = s.floorplans;
  const floor = fp?.[activeFloor];
  const archImgs = [`${API}/api/uploads/dl-1.jpeg`, `${API}/api/uploads/dl-2.jpeg`, `${API}/api/uploads/dl-3.jpeg`]

  return (
    <>
      {/* Loader */}
      <div className={`nv-loader ${loaderDone ? 'out' : ''}`}>
        <div className="nv-loader-logo" data-testid="loader-logo">
          <img src={FULL_LOGO} alt="Navanaa Estate" className="nv-loader-logo-img" />
        </div>
        <div className="nv-loader-bar" />
      </div>

      {/* Mobile Menu */}
      <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} go={go} />

      {/* Nav */}
      <nav className={`nv-nav ${scrolled ? 'scrolled' : ''}`} data-testid="nav">
        <span className="nv-logo" onClick={() => go('hero')}>
          <img
            src={scrolled ? `${API}/api/uploads/navanaa_logo_clean-dark.png` : FULL_LOGO}
            alt="Navanaa Estate"
            className="nv-logo-img"
            data-testid="nav-logo"
          />
        </span>

        {/* Desktop links */}
        <div className="nv-nav-links-wrap">
          <ul className="nv-links">
            <li><a onClick={() => go('about')}>About</a></li>
            <li><a onClick={() => go('estate')}>Estate</a></li>
            <li><a onClick={() => go('architecture')}>Architecture</a></li>
            <li><a onClick={() => go('gallery')}>Gallery</a></li>
            <li><a onClick={() => go('location')}>Location</a></li>
            <li><Link to="/projects">Projects</Link></li>
          </ul>
        </div>

        {/* Desktop CTA */}
        <a className="nv-nav-cta" onClick={() => go('contact')} data-testid="nav-contact-btn"
          style={{ display: 'none' }}
          ref={el => { if (el) el.style.display = ''; }}
        >
          Enquire
        </a>

        {/* Hamburger */}
        <button
          className={`nv-hamburger ${menuOpen ? 'open' : ''}`}
          onClick={() => setMenuOpen(v => !v)}
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
        >
          <span /><span /><span />
        </button>
      </nav>

      {/* Desktop CTA rendered properly */}
      <style>{`.nv-nav-links-wrap ~ .nv-nav-cta-desktop { display: flex; }`}</style>

      <HeroSection data={s.hero} go={go} />

       {/* Video */}
      <section className="nv-video-showcase" data-testid="video-section">
        <div className="nv-section-inner" style={{ textAlign: 'center' }}>
          <div className="nv-eyebrow center nv-reveal">Virtual Walkthrough</div>
          <h2 className="nv-h2 light nv-reveal" style={{ textAlign: 'center' }}>Experience the <em>Estate</em></h2>
          <p className="nv-body light nv-reveal" style={{ textAlign: 'center', margin: '0 auto 36px' }}>
            An AI-generated cinematic walkthrough of Navanaa Estate.
          </p>
          <div className="nv-video-player nv-reveal">
            <video
              src={`https://pub-c34aedad6b2e43ce903e45d5e600e33d.r2.dev/navanaa/navanaa.webm`}
               autoPlay muted loop playsInline
              data-testid="ai-walkthrough-video"
            />
          </div>
        </div>
      </section>

      <WhyNavanaaSection />

      {/* Stats */}
      <div className="nv-stats" data-testid="stats-section">
        {s.stats?.items?.map((it) => (
          <div className="nv-stat" key={`stat-${it.number}-${it.label}`}>
            <span className="nv-stat-num">{it.number}</span>
            <span className="nv-stat-lbl">{it.label}</span>
          </div>
        ))}
      </div>

      <TrustSection data={s.trust} />

      {/* About */}
      <section className="nv-about" id="about" data-testid="about-section">
        <div className="nv-section-inner">
          <div className="nv-about-img nv-reveal">
            <Img src={"https://navanaaestate.com/api/uploads/villa.webp"} alt="Navanaa Estate" text="Navanaa Estate" />
            <div className="nv-about-accent" />
          </div>
          <div>
            <div className="nv-eyebrow nv-reveal">About Navanaa</div>
            <h2 className="nv-h2 dark nv-reveal">{s.about?.title || "Built for Those Who've Outgrown the City"}</h2>
            <p className="nv-body nv-reveal">{s.about?.body1}</p>
            <p className="nv-body nv-reveal" style={{ marginTop: 14 }}><em>{s.about?.body2}</em></p>
            <p className="nv-body nv-reveal" style={{ marginTop: 14 }}>{s.about?.body3}</p>
            <div className="nv-vals nv-reveal">
              {s.about?.values?.map((v) => (
                <div className="nv-val" key={`val-${v.num}`}>
                  <div className="nv-val-n">{v.num}</div>
                  <div>
                    <span className="nv-val-title">{v.title}</span>
                    <span className="nv-val-desc">{v.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Langha */}
      <section className="nv-langha" data-testid="langha-section">
        <div className="nv-section-inner">
          <div>
            <div className="nv-eyebrow nv-reveal">The Langha Life</div>
            <h2 className="nv-h2 light nv-reveal">{s.langha?.title || 'Langha, Dehradun'}</h2>
            <p className="nv-body light nv-reveal" style={{ whiteSpace: 'pre-line' }}>{s.langha?.body}</p>
            <div className="nv-loc-tags nv-reveal">
              {s.langha?.tags?.map((t, i) => <div className="nv-loc-tag" key={`langha-tag-${i}`}>{t}</div>)}
            </div>
          </div>
          <div className="nv-langha-img nv-reveal">
            <Img src={"https://pub-c34aedad6b2e43ce903e45d5e600e33d.r2.dev/navanaa/langha.webp"} alt="Langha" text="Himalayan Landscape" />
          </div>
        </div>
      </section>

      {/* Estate */}
      <section className="nv-estate" id="estate" data-testid="estate-section">
        <div className="nv-section-inner">
          <div>
            <div className="nv-eyebrow nv-reveal">The Project</div>
            <h2 className="nv-h2 dark nv-reveal">{s.estate?.title || 'Navanaa Estate'}</h2>
            <p className="nv-body nv-reveal" style={{ whiteSpace: 'pre-line' }}>{s.estate?.body}</p>
            <div className="nv-estate-stats nv-reveal">
              {s.estate?.stats?.map((st) => (
                <div className="nv-estate-stat" key={`estat-${st.number}`}>
                  <div className="nv-estate-stat-num">{st.number}</div>
                  <div className="nv-estate-stat-lbl">{st.label}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="nv-estate-img-wrap nv-reveal">
            <Img src={"https://pub-c34aedad6b2e43ce903e45d5e600e33d.r2.dev/navanaa/house.webp"} alt="Estate" text="Estate Render" />
            {s.estate?.badge_number && (
              <div className="nv-estate-badge">
                <div className="nv-estate-badge-num">{s.estate.badge_number}</div>
                <div className="nv-estate-badge-lbl">{s.estate.badge_label}</div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Architecture */}
      <section className="nv-arch" id="architecture" data-testid="architecture-section">
        <div className="nv-section-inner">
          <div>
            <div className="nv-eyebrow nv-reveal">Design Language</div>
            <h2 className="nv-h2 light nv-reveal">{s.architecture?.title || 'Rooted in Himalayan Intelligence'}</h2>
            <p className="nv-body light nv-reveal" style={{ whiteSpace: 'pre-line' }}>{s.architecture?.body}</p>
            <div className="nv-arch-points nv-reveal">
              {s.architecture?.points?.map((p) => (
                <div className="nv-arch-point" key={`arch-${p.title}`}>
                  <div className="nv-arch-point-title">{p.title}</div>
                  <div className="nv-arch-point-desc">{p.desc}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="nv-arch-gallery nv-reveal">
            {archImgs.map((img, i) => (
              <div className={`nv-arch-img ${i === 0 ? 'tall' : ''}`} key={`arch-img-${i}`}>
                <Img src={img} alt={`Architecture ${i + 1}`} text="Architecture" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Materials */}
      <section className="nv-mats" data-testid="materials-section">
        <div className="nv-section-inner">
          <div className="nv-eyebrow center nv-reveal">Material Palette</div>
          <h2 className="nv-h2 dark nv-reveal" style={{ textAlign: 'center' }}>{s.materials?.title || 'Luxury That Ages, Not Fades'}</h2>
          <p className="nv-body nv-reveal" style={{ textAlign: 'center', margin: '0 auto' }}>{s.materials?.subtitle}</p>
          <div className="nv-mats-grid nv-reveal">
            {s.materials?.items?.map((mat) => (
              <div className="nv-mat-card" key={`mat-${mat.title}`}>
                <div className="nv-mat-icon"><SysIcon name={mat.icon} /></div>
                <div className="nv-mat-title">{mat.title}</div>
                <ul className="nv-mat-items">
                  {mat.points?.map((p, j) => <li key={`mp-${j}`}>{p}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="nv-gallery" id="gallery" data-testid="gallery-section">
        <div className="nv-section-inner">
          <div className="nv-eyebrow center nv-reveal">Gallery</div>
          <h2 className="nv-h2 light nv-reveal" style={{ textAlign: 'center' }}>Estate <em>Gallery</em></h2>
          <div className="nv-gallery-grid nv-reveal">
            {gallery.length > 0
              ? gallery.map((it) => (
                <div className="nv-gallery-item" key={`gal-${it.id || it.title}`}>
                  <img src={resolveUrl(it.image_url)} alt={it.title} />
                  <div className="nv-gallery-label">
                    <div className="nv-gallery-label-title">{it.title}</div>
                    {it.description && <div className="nv-gallery-label-desc">{it.description}</div>}
                  </div>
                </div>
              ))
              : <div className="nv-gallery-empty">Gallery images will be added soon.</div>
            }
          </div>
        </div>
      </section>

     

      {/* Floor Plans */}
      {fp && (
        <section className="nv-floors" id="floorplans" data-testid="floorplans-section">
          <div className="nv-section-inner">
            <div className="nv-eyebrow center nv-reveal">Spatial Design</div>
            <h2 className="nv-h2 light nv-reveal" style={{ textAlign: 'center' }}>
              {fp.title || 'Designed for Flow, Not Just Function'}
            </h2>
            <p className="nv-body light nv-reveal" style={{ textAlign: 'center', margin: '0 auto 36px' }}>
              {fp.subtitle}
            </p>
            <div className="nv-floor-tabs-wrap nv-reveal">
              <div className="nv-floor-tabs">
                <button className={`nv-floor-tab ${activeFloor === 'ground' ? 'active' : ''}`} onClick={() => setActiveFloor('ground')}>
                  Ground Floor
                </button>
                <button className={`nv-floor-tab ${activeFloor === 'first' ? 'active' : ''}`} onClick={() => setActiveFloor('first')}>
                  First Floor
                </button>
              </div>
            </div>
            <div className="nv-floor-panel nv-reveal">
              <div className="nv-plan-wrap">
                <div className="nv-plan-top">
                  <span className="nv-plan-label">Architectural Drawing &middot; {floor?.tag} Plan</span>
                  <span className="nv-plan-north">N &uarr;</span>
                </div>
                <div className="nv-plan-img">
                  <img src={activeFloor === 'ground' ? FLOOR_GF_URL : FLOOR_FF_URL} alt={`${floor?.tag} Plan`} />
                </div>
                <div className="nv-plan-caption">
                  {floor?.tag} Plan ({floor?.area}) &middot; Villa Estate, Langa-Bhurd
                </div>
              </div>
              <div>
                <div className="nv-floor-header">
                  <div className="nv-floor-tag">{floor?.tag}</div>
                  <div className="nv-floor-title">{floor?.title}</div>
                  <div className="nv-floor-area">Total Built-up: {floor?.area}</div>
                </div>
                <div className="nv-rooms">
                  {floor?.rooms?.map((r) => (
                    <div className="nv-room" key={`room-${r.name}`}>
                      <div>
                        <div className="nv-room-name">{r.name}</div>
                        <div className="nv-room-desc">{r.desc}</div>
                      </div>
                      <div className="nv-room-sqft">{r.sqft}<small>sq. ft.</small></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Smart Systems */}
      <section className="nv-sys" data-testid="systems-section">
        <div className="nv-section-inner">
          <div>
            <div className="nv-eyebrow nv-reveal">Intelligent Living</div>
            <h2 className="nv-h2 light nv-reveal">{s.systems?.title || 'A Home That Thinks Ahead'}</h2>
            <p className="nv-body light nv-reveal">{s.systems?.subtitle}</p>
            <div className="nv-sys-grid nv-reveal" style={{ marginTop: 40 }}>
              {s.systems?.items?.map((it) => (
                <div className={`nv-sys-card ${it.badge ? 'wide' : ''}`} key={`sys-${it.title}`}>
                  <div className="nv-sys-icon"><SysIcon name={it.icon} /></div>
                  <div>
                    <div className="nv-sys-title">{it.title}</div>
                    <div className="nv-sys-desc">{it.desc}</div>
                    {it.badge && <div className="nv-sys-badge">{it.badge}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="nv-sys-img nv-reveal">
            <Img src={s.systems?.image} alt="Intelligent Living" text="Smart Living" />
          </div>
        </div>
      </section>

      {/* Location */}
      <section className="nv-location" id="location" data-testid="location-section">
        <div className="nv-section-inner">
          <div>
            <div className="nv-eyebrow nv-reveal">Location Highlights</div>
            <h2 className="nv-h2 light nv-reveal">{s.location?.title || 'Close Enough. Far Enough.'}</h2>
            <p className="nv-body light nv-reveal">{s.location?.body}</p>
            {s.location?.video && (
              <div className="nv-location-video nv-reveal">
                <video src={"https://pub-c34aedad6b2e43ce903e45d5e600e33d.r2.dev/navanaa/aerial.webm"} controls muted autoPlay loop playsInline data-testid="location-video" />
                <div className="nv-location-video-label">Aerial View of the Estate</div>
              </div>
            )}
            <div className="nv-loc-closing nv-reveal"><p>{s.location?.quote}</p></div>
          </div>
          <div className="nv-reveal">
            {s.location?.groups?.map((grp) => (
              <div key={`loc-${grp.title}`} style={{ marginBottom: 32 }}>
                <div className="nv-loc-col-title">{grp.title}</div>
                <div className="nv-loc-items">
                  {grp.items?.map((it, j) => (
                    <div className="nv-loc-item" key={`locit-${grp.title}-${j}`}>
                      <span className="nv-loc-item-name">{it[0]}</span>
                      <span className="nv-loc-item-time">{it[1]}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Difference */}
      <section className="nv-diff" data-testid="difference-section">
        <div className="nv-section-inner">
          <div className="nv-diff-intro">
            <div>
              <div className="nv-eyebrow nv-reveal">The Navanaa Difference</div>
              <h2 className="nv-h2 dark nv-reveal">{s.difference?.title || 'Not Another Dehradun Villa Project'}</h2>
            </div>
            <p className="nv-body nv-reveal">{s.difference?.body}</p>
          </div>
          <div className="nv-diff-grid nv-reveal">
            {s.difference?.cards?.map((c) => (
              <div className="nv-diff-card" key={`diff-${c.title}`}>
                <div className="nv-diff-card-title">{c.title}</div>
                <div className="nv-diff-card-body">{c.desc}</div>
              </div>
            ))}
          </div>
          <div className="nv-comp nv-reveal">
            <div className="nv-comp-col feat">
              <div className="nv-comp-col-title">Navanaa Estate</div>
              {['Dhaji Dewari seismic construction', '7 AI-powered intelligent systems', 'Only 3 exclusive plots total', '3,340 sq. ft. designed space', 'Yamuna river stone + timber', '24°C climate maintained always', 'Google AI entertainment', 'Full property management'].map((x) => (
                <div className="nv-comp-item" key={`comp-n-${x}`}>
                  <Check size={13} style={{ color: 'var(--gold)', flexShrink: 0, marginTop: 2 }} />{x}
                </div>
              ))}
            </div>
            <div className="nv-comp-col">
              <div className="nv-comp-col-title">Standard Hill Villas</div>
              {['Concrete, no heritage', 'No smart systems', 'Mass-developed plots', 'Generic layouts', 'Synthetic finishes'].map((x) => (
                <div className="nv-comp-item" key={`comp-s-${x}`}>
                  <span style={{ color: '#c0392b', flexShrink: 0 }}>&#10007;</span>{x}
                </div>
              ))}
            </div>
            <div className="nv-comp-col">
              <div className="nv-comp-col-title">Generic Luxury</div>
              {['Design-light, amenity-heavy', 'No architectural identity', 'Overdeveloped density', 'No heritage techniques', 'No intelligent systems'].map((x) => (
                <div className="nv-comp-item" key={`comp-g-${x}`}>
                  <span style={{ color: '#c0392b', flexShrink: 0 }}>&#10007;</span>{x}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Amenities */}
      {s.amenities && (
        <section className="nv-amen" data-testid="amenities-section">
          <div className="nv-section-inner">
            <div className="nv-amen-img nv-reveal">
              <Img src={s.amenities.image} alt="Navanaa Estate" text="Navanaa Estate" />
            </div>
            <div>
              <div className="nv-eyebrow nv-reveal">What Your Home Includes</div>
              <h2 className="nv-h2 light nv-reveal">{s.amenities.title || 'Amenities'}</h2>
              <p className="nv-body light nv-reveal">{s.amenities.subtitle}</p>
              <div className="nv-amen-grid nv-reveal">
                {s.amenities.groups?.map((grp) => (
                  <div key={`amen-${grp.title}`}>
                    <div className="nv-amen-group-title">{grp.title}</div>
                    <ul className="nv-amen-items">
                      {grp.items?.map((x, j) => <li key={`ai-${j}`}>{x}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
              <div className="nv-amen-note">*All specifications subject to final design drawings.</div>
            </div>
          </div>
        </section>
      )}

      {/* Property Management */}
      {s.propman && (
        <section className="nv-pm" data-testid="propman-section">
          <div className="nv-section-inner">
            <div>
              <div className="nv-eyebrow nv-reveal">Beyond the Home</div>
              <h2 className="nv-h2 dark nv-reveal">{s.propman.title || 'Property Management Services'}</h2>
              <p className="nv-body nv-reveal">{s.propman.subtitle}</p>
              <div className="nv-pm-staff nv-reveal">
                {s.propman.staff?.map((x) => <div className="nv-pm-staff-item" key={`staff-${x}`}>{x}</div>)}
              </div>
              <div className="nv-pm-opt-title nv-reveal">Optional Services</div>
              <ul className="nv-pm-opt-items nv-reveal">
                {s.propman.optional?.map((x, i) => <li key={`opt-${i}`}>{x}</li>)}
              </ul>
              <div className="nv-sust-title nv-reveal">Sustainability Commitment</div>
              <ul className="nv-sust-items nv-reveal">
                {s.propman.sustainability?.map((x, i) => <li key={`sust-${i}`}>{x}</li>)}
              </ul>
            </div>
            <div className="nv-pm-visual nv-reveal">
              <Img src={"https://pub-c34aedad6b2e43ce903e45d5e600e33d.r2.dev/navanaa/house.webp"} alt="Estate" text="Navanaa Estate" />
            </div>
          </div>
        </section>
      )}

      {/* Lifestyle */}
      {s.lifestyle && (
        <section className="nv-lifestyle" data-testid="lifestyle-section">
          <div className="nv-section-inner">
            <div className="nv-eyebrow center nv-reveal">What Life Looks Like Here</div>
            <h2 className="nv-h2 light nv-reveal" style={{ textAlign: 'center' }}>
              {s.lifestyle.title || "This Is Not an Upgrade. It's an Exit."}
            </h2>
            <p className="nv-body light nv-reveal" style={{ textAlign: 'center', margin: '0 auto' }}>
              {s.lifestyle.subtitle}
            </p>
            <div className="nv-moments nv-reveal">
              {s.lifestyle.moments?.map((m, i) => (
                <div className="nv-moment" key={`moment-${m.time}`}>
                  <div className="nv-moment-bg" style={{ backgroundImage: `url(${lifestyleImages[i]})` }} />
                  <div className="nv-moment-content">
                    <div className="nv-moment-time">{m.time}</div>
                    <div className="nv-moment-title">{m.title}</div>
                    <div className="nv-moment-desc">{m.desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="nv-for-whom nv-reveal">
              {s.lifestyle.for_whom?.map((x) => <div className="nv-for-whom-item" key={`fw-${x}`}>{x}</div>)}
            </div>
          </div>
        </section>
      )}

      <FoundersSection data={s.founders} />

      {/* Investment */}
      <section className="nv-inv" data-testid="investment-section">
        <div className="nv-section-inner">
          <div className="nv-inv-visual nv-reveal">
            <div className="nv-inv-main">
              <Img src={s.investment?.image} alt="Investment" text="Navanaa Estate" />
            </div>
            {s.investment?.badge_number && (
              <div className="nv-inv-badge">
                <div className="nv-inv-badge-num">{s.investment.badge_number}</div>
                <div className="nv-inv-badge-lbl">{s.investment.badge_label}</div>
              </div>
            )}
          </div>
          <div>
            <div className="nv-eyebrow nv-reveal">The Value Case</div>
            <h2 className="nv-h2 dark nv-reveal">{s.investment?.title || 'Scarcity Creates Value'}</h2>
            <p className="nv-body nv-reveal">{s.investment?.body}</p>
            <div className="nv-inv-points nv-reveal">
              {s.investment?.points?.map((p) => (
                <div className="nv-inv-point" key={`inv-${p.title}`}>
                  <div className="nv-inv-point-title">{p.title}</div>
                  <div className="nv-inv-point-desc">{p.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="nv-cta-section" data-testid="cta-section">
        <div className="nv-cta-eyebrow">{s.cta?.eyebrow}</div>
        <h2 className="nv-cta-h">{s.cta?.title || 'Some Homes Are Bought. Some Are Chosen.'}</h2>
        <div className="nv-cta-div" />
        <p className="nv-cta-sub">{s.cta?.subtitle}</p>
        <a className="nv-btn-gold" onClick={() => go('contact')}>Know More</a>
      </section>

      <ContactSection
        data={s.contact}
        cf={cf} setCf={setCf}
        onSubmit={submitContact}
        cfLoading={cfLoading}
        cfMsg={cfMsg}
      />

      {/* Footer */}
      <footer className="nv-footer" data-testid="footer">
        <div>
          <div className="nv-footer-brand">
            <img src={FULL_LOGO} alt="Navanaa Estate" className="nv-footer-logo" />
          </div>
          <div className="nv-footer-tagline">{s.footer?.tagline}</div>
        </div>
        <div>
          <div className="nv-footer-col-title">Quick Links</div>
          <ul className="nv-footer-links">
            <li><a onClick={() => go('about')}>About</a></li>
            <li><a onClick={() => go('estate')}>The Estate</a></li>
            <li><a onClick={() => go('architecture')}>Architecture</a></li>
            <li><a onClick={() => go('gallery')}>Gallery</a></li>
          </ul>
        </div>
        <div>
          <div className="nv-footer-col-title">More</div>
          <ul className="nv-footer-links">
            <li><a onClick={() => go('floorplans')}>Floor Plans</a></li>
            <li><a onClick={() => go('location')}>Location</a></li>
            <li><Link to="/projects">Our Projects</Link></li>
            <li><a onClick={() => go('contact')}>Contact</a></li>
          </ul>
        </div>
        <div>
          <div className="nv-footer-col-title">Legal</div>
          <ul className="nv-footer-links">
            <li><a>Privacy Policy</a></li>
            <li><a>Terms of Service</a></li>
          </ul>
        </div>
      </footer>
      <div className="nv-footer-bottom">
        <p>&copy; {s.footer?.copyright}</p>
        <p>{s.footer?.tagline}</p>
      </div>
    </>
  );
}